/**
 * VendorDrawer Component
 * Create/Edit Vendor form
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import { Building2, Globe, Mail, MapPin, Phone, Tag, User, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import {
  useCreateVendorMutation,
  useGetNextVendorCodeQuery,
  useUpdateVendorMutation,
} from '../../services/vendorApi';
import type {
  CreateVendorRequest,
  UpdateVendorRequest,
  Vendor,
  VendorType,
} from '../../types/vendor';
import { Dropdown, Input, PhoneInput, TextArea } from '../forms';

// Props interface
export interface VendorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  vendor?: Vendor | null;
  mode: 'create' | 'edit';
  onSuccess?: () => void;
}

// Form data type
interface VendorFormData {
  code: string;
  name: string;
  type: VendorType;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactTitle: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  taxId: string;
  paymentTerms: string;
  website: string;
  notes: string;
}

// Validation schema
const vendorSchema = Yup.object({
  code: Yup.string().max(50, 'Code cannot exceed 50 characters').optional(),
  name: Yup.string()
    .required('Vendor name is required')
    .min(1, 'Vendor name is required')
    .max(200, 'Name cannot exceed 200 characters'),
  type: Yup.string()
    .oneOf(['supplier', 'subcontractor', 'equipment', 'professional', 'utility', 'other'])
    .required('Vendor type is required'),
  contactName: Yup.string()
    .required('Contact name is required')
    .max(200, 'Contact name cannot exceed 200 characters'),
  contactEmail: Yup.string()
    .email('Invalid email address')
    .required('Contact email is required')
    .max(255, 'Email cannot exceed 255 characters'),
  contactPhone: Yup.string()
    .required('Contact phone is required')
    .max(50, 'Phone cannot exceed 50 characters'),
  contactTitle: Yup.string().max(100, 'Title cannot exceed 100 characters').optional(),
  street: Yup.string().max(255, 'Street cannot exceed 255 characters').optional(),
  city: Yup.string().max(100, 'City cannot exceed 100 characters').optional(),
  state: Yup.string().max(100, 'State cannot exceed 100 characters').optional(),
  zipCode: Yup.string().max(20, 'Zip code cannot exceed 20 characters').optional(),
  country: Yup.string().max(100, 'Country cannot exceed 100 characters').optional(),
  taxId: Yup.string().max(50, 'Tax ID cannot exceed 50 characters').optional(),
  paymentTerms: Yup.string().max(100, 'Payment terms cannot exceed 100 characters').optional(),
  website: Yup.string()
    .url('Invalid website URL')
    .max(500, 'Website cannot exceed 500 characters')
    .nullable()
    .optional(),
  notes: Yup.string().max(2000, 'Notes cannot exceed 2000 characters').optional(),
});

// Vendor type options
const VENDOR_TYPE_OPTIONS = [
  { value: 'supplier', label: 'Supplier' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'equipment', label: 'Equipment Rental' },
  { value: 'professional', label: 'Professional Services' },
  { value: 'utility', label: 'Utility Provider' },
  { value: 'other', label: 'Other' },
];

// Payment terms options
const PAYMENT_TERMS_OPTIONS = [
  { value: 'Net 15', label: 'Net 15' },
  { value: 'Net 30', label: 'Net 30' },
  { value: 'Net 45', label: 'Net 45' },
  { value: 'Net 60', label: 'Net 60' },
  { value: 'Due on Receipt', label: 'Due on Receipt' },
  { value: 'COD', label: 'Cash on Delivery' },
  { value: '2/10 Net 30', label: '2/10 Net 30 (2% discount if paid within 10 days)' },
];

// Default form values
const defaultValues: VendorFormData = {
  code: '',
  name: '',
  type: 'supplier',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  contactTitle: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  taxId: '',
  paymentTerms: 'Net 30',
  website: '',
  notes: '',
};

const VendorDrawer: React.FC<VendorDrawerProps> = ({
  isOpen,
  onClose,
  vendor,
  mode,
  onSuccess,
}) => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API mutations
  const [createVendor] = useCreateVendorMutation();
  const [updateVendor] = useUpdateVendorMutation();

  // Get next vendor code for new vendors
  const { data: nextCodeResponse } = useGetNextVendorCodeQuery(undefined, {
    skip: mode === 'edit',
  });

  // Form setup
  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<VendorFormData>({
    resolver: yupResolver(vendorSchema) as any,
    defaultValues,
  });

  // Set form values when vendor changes or drawer opens
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && vendor) {
        // Populate form with existing vendor data
        reset({
          code: vendor.code || '',
          name: vendor.name || '',
          type: vendor.type || 'supplier',
          contactName: vendor.contact?.name || '',
          contactEmail: vendor.contact?.email || '',
          contactPhone: vendor.contact?.phone || '',
          contactTitle: vendor.contact?.title || '',
          street: vendor.address?.street || '',
          city: vendor.address?.city || '',
          state: vendor.address?.state || '',
          zipCode: vendor.address?.zipCode || '',
          country: vendor.address?.country || '',
          taxId: vendor.taxId || '',
          paymentTerms: vendor.paymentTerms || 'Net 30',
          website: vendor.website || '',
          notes: vendor.notes || '',
        });
      } else {
        // Reset to defaults for create mode
        reset(defaultValues);
        // Set auto-generated code
        if (nextCodeResponse?.data) {
          setValue('code', nextCodeResponse.data);
        }
      }
    }
  }, [isOpen, mode, vendor, reset, setValue, nextCodeResponse]);

  // Handle form submit
  const onSubmit = async (data: VendorFormData) => {
    setIsSubmitting(true);

    try {
      if (mode === 'create') {
        const createData: CreateVendorRequest = {
          code: data.code || undefined,
          name: data.name,
          type: data.type,
          contact: {
            name: data.contactName,
            email: data.contactEmail,
            phone: data.contactPhone,
            title: data.contactTitle || undefined,
          },
          address: {
            street: data.street || undefined,
            city: data.city || undefined,
            state: data.state || undefined,
            zipCode: data.zipCode || undefined,
            country: data.country || undefined,
          },
          taxId: data.taxId || undefined,
          paymentTerms: data.paymentTerms || undefined,
          website: data.website || undefined,
          notes: data.notes || undefined,
        };

        const result = await createVendor(createData).unwrap();

        if (result.success) {
          toast.success('Vendor created successfully');
          onSuccess?.();
          onClose();
        } else {
          toast.error(result.message || 'Failed to create vendor');
        }
      } else if (mode === 'edit' && vendor) {
        const updateData: UpdateVendorRequest = {
          code: data.code || undefined,
          name: data.name,
          type: data.type,
          contact: {
            name: data.contactName,
            email: data.contactEmail,
            phone: data.contactPhone,
            title: data.contactTitle || undefined,
          },
          address: {
            street: data.street || undefined,
            city: data.city || undefined,
            state: data.state || undefined,
            zipCode: data.zipCode || undefined,
            country: data.country || undefined,
          },
          taxId: data.taxId || undefined,
          paymentTerms: data.paymentTerms || undefined,
          website: data.website || undefined,
          notes: data.notes || undefined,
        };

        const result = await updateVendor({
          vendorId: vendor._id,
          data: updateData,
        }).unwrap();

        if (result.success) {
          toast.success('Vendor updated successfully');
          onSuccess?.();
          onClose();
        } else {
          toast.error(result.message || 'Failed to update vendor');
        }
      }
    } catch (error: any) {
      console.error('Error saving vendor:', error);
      toast.error(error.data?.message || 'Failed to save vendor');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {mode === 'create' ? 'New Vendor' : 'Edit Vendor'}
                </h2>
                <p className="text-sm text-gray-500">
                  {mode === 'create'
                    ? 'Add a new vendor to your organization'
                    : `Editing ${vendor?.name}`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto px-8 py-6">
              {/* Basic Information */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Vendor Code"
                      placeholder="Auto-generated if empty"
                      error={errors.code?.message}
                      {...register('code')}
                    />
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          label="Vendor Type"
                          options={VENDOR_TYPE_OPTIONS}
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.type?.message}
                          required
                        />
                      )}
                    />
                    <div className="col-span-2">
                      <Input
                        label="Vendor Name"
                        placeholder="Enter company name"
                        error={errors.name?.message}
                        required
                        {...register('name')}
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Contact Name"
                      placeholder="Primary contact person"
                      error={errors.contactName?.message}
                      required
                      {...register('contactName')}
                    />
                    <Input
                      label="Title"
                      placeholder="e.g., Sales Manager"
                      error={errors.contactTitle?.message}
                      {...register('contactTitle')}
                    />
                    <Input
                      label="Email"
                      type="email"
                      placeholder="contact@company.com"
                      error={errors.contactEmail?.message}
                      required
                      leftElement={<Mail className="w-4 h-4 text-gray-400 ml-3" />}
                      {...register('contactEmail')}
                    />
                    <Controller
                      name="contactPhone"
                      control={control}
                      render={({ field }) => (
                        <PhoneInput
                          label="Phone"
                          value={field.value}
                          onChange={value => field.onChange(value)}
                          error={errors.contactPhone?.message}
                          required
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Address
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Input
                        label="Street Address"
                        placeholder="123 Main Street"
                        error={errors.street?.message}
                        {...register('street')}
                      />
                    </div>
                    <Input
                      label="City"
                      placeholder="City"
                      error={errors.city?.message}
                      {...register('city')}
                    />
                    <Input
                      label="State/Province"
                      placeholder="State"
                      error={errors.state?.message}
                      {...register('state')}
                    />
                    <Input
                      label="Zip/Postal Code"
                      placeholder="12345"
                      error={errors.zipCode?.message}
                      {...register('zipCode')}
                    />
                    <Input
                      label="Country"
                      placeholder="Country"
                      error={errors.country?.message}
                      {...register('country')}
                    />
                  </div>
                </div>

                {/* Business Details */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Business Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Tax ID / EIN"
                      placeholder="XX-XXXXXXX"
                      error={errors.taxId?.message}
                      {...register('taxId')}
                    />
                    <Controller
                      name="paymentTerms"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          label="Payment Terms"
                          options={PAYMENT_TERMS_OPTIONS}
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.paymentTerms?.message}
                        />
                      )}
                    />
                    <div className="col-span-2">
                      <Input
                        label="Website"
                        placeholder="https://www.company.com"
                        error={errors.website?.message}
                        leftElement={<Globe className="w-4 h-4 text-gray-400 ml-3" />}
                        {...register('website')}
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        label="Notes"
                        placeholder="Additional information about this vendor..."
                        error={errors.notes?.message}
                        rows={4}
                        value={field.value || ''}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-8 py-4 bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting
                  ? mode === 'create'
                    ? 'Creating...'
                    : 'Saving...'
                  : mode === 'create'
                    ? 'Create Vendor'
                    : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
};

export default VendorDrawer;
